import React, { useEffect, useState, useRef } from 'react'
import Api from '../../../../_helpers/Api'
import Skeleton from 'react-loading-skeleton'
import { trans } from '../../../../_providers/Translation'
import PartsList from './PartsList'
import '../../../../assets/css/partials/status-diagram.css'
import '../../../../assets/css/partials/StoragePartsStats.css'
import { PieChart } from '@mui/x-charts'
import { Button } from '@mui/material'
import CompactFilterLayout from '@/components/partials/CompactFilterLayout'
import DateRange from '@/components/partials/DateRange'
import filterImage from '@/assets/img/icons/filter.svg'
import AddOrEdit from '../../storage/partials/AddOrEdit'

const INITIAL_FILTERS = {
    date_from: null,
    date_to: null,
}

// how many shades we generate from a single base color
const VARIATIONS_PER_COLOR = 5

function hexToHsl(hex) {
    let value = hex.replace('#', '')
    if (value.length === 3) {
        value = value.split('').map(c => c + c).join('')
    }

    const r = parseInt(value.slice(0, 2), 16) / 255
    const g = parseInt(value.slice(2, 4), 16) / 255
    const b = parseInt(value.slice(4, 6), 16) / 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const delta = max - min

    let h = 0
    if (delta !== 0) {
        if (max === r) h = ((g - b) / delta) % 6
        else if (max === g) h = (b - r) / delta + 2
        else h = (r - g) / delta + 4
        h *= 60
        if (h < 0) h += 360
    }

    const l = (max + min) / 2
    const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

    return { h, s: s * 100, l: l * 100 }
}

function hslToHex(h, s, l) {
    s /= 100
    l /= 100

    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l - c / 2

    let r = 0
    let g = 0
    let b = 0

    if (h < 60) [r, g, b] = [c, x, 0]
    else if (h < 120) [r, g, b] = [x, c, 0]
    else if (h < 180) [r, g, b] = [0, c, x]
    else if (h < 240) [r, g, b] = [0, x, c]
    else if (h < 300) [r, g, b] = [x, 0, c]
    else [r, g, b] = [c, 0, x]

    const toHex = v => Math.round((v + m) * 255).toString(16).padStart(2, '0')

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

export default function StoragePartsStats({
    endpoint = 'dashboard/storage-parts/stats',
    availabilityEndpoint = 'dashboard/storage-parts/availability',
    colors = ['#22C55E', '#3B82F6', '#8B5CF6', '#EF4444', '#F59E0B', '#EC4899', '#10B981', '#6366F1'],
    size = 220,
    donut = true
}) {
    const [filters, setFilters] = useState(INITIAL_FILTERS)

    const compactFilterLayoutRef = useRef(null)
    const fetchDebounceRef = useRef(null)
    const availabilityDebounceRef = useRef(null)
    const addOrEditRef = useRef(null)

    // Returns a generated color for the given index. Each base color from `colors`
    // produces VARIATIONS_PER_COLOR shades — as the index grows within a color group
    // its intensity is lowered (less saturated, lighter).
    function getColor(index) {
        const baseColor = colors[Math.floor(index / VARIATIONS_PER_COLOR) % colors.length]
        const variation = index % VARIATIONS_PER_COLOR

        const { h, s, l } = hexToHsl(baseColor)

        // variation 0 keeps the original color; each step reduces intensity
        const factor = variation / (VARIATIONS_PER_COLOR - 1)
        const newS = s * (1 - factor * 0.6)
        const newL = Math.min(l + factor * 28, 92)

        return hslToHex(h, newS, newL)
    }

    function handleFilterChange(name, value) {
        setFilters(prev => ({ ...prev, [name]: value }))
    }

    function getAppliedFiltersCount() {
        return filters.date_from || filters.date_to ? 1 : 0
    }

    const [refreshKey, setRefreshKey] = useState(0)

    const handleCreate = () => setRefreshKey(prev => prev + 1)
    const handleUpdate = () => setRefreshKey(prev => prev + 1)

    // left side — existing stats API
    const [state, setState] = useState({ loading: true, data: null })

    useEffect(() => {
        clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = setTimeout(() => {
            setState(prev => ({ ...prev, loading: true }))

            let mounted = true
            Api.get(`${endpoint}?date_from=${filters.date_from || ''}&date_to=${filters.date_to || ''}`)
                .then(res => {
                    if (!mounted) return
                    setState({ loading: false, data: res?.data ?? res })
                })
                .catch(() => {
                    if (mounted) setState({ loading: false, data: null })
                })
            return () => { mounted = false }
        }, 300)
        return () => clearTimeout(fetchDebounceRef.current)
    }, [endpoint, filters, refreshKey])

    const apiData = state.data || {}
    const recentParts = Array.isArray(apiData.recent_parts) ? apiData.recent_parts : []

    // right side — new availability API
    const [locationFilter, setLocationFilter] = useState('warehouse') // 'warehouse', 'van'
    const [availabilityState, setAvailabilityState] = useState({ loading: true, data: null })

    useEffect(() => {
        clearTimeout(availabilityDebounceRef.current)
        availabilityDebounceRef.current = setTimeout(() => {
            let mounted = true
            setAvailabilityState(prev => ({ ...prev, loading: true }))

            Api.get(`${availabilityEndpoint}?location_type=${locationFilter}&date_from=${filters.date_from || ''}&date_to=${filters.date_to || ''}`)
                .then(res => {
                    if (!mounted) return
                    setAvailabilityState({ loading: false, data: res?.data ?? null })
                })
                .catch(() => {
                    if (mounted) setAvailabilityState({ loading: false, data: null })
                })

            return () => { mounted = false }
        }, 300)
        return () => clearTimeout(availabilityDebounceRef.current)
    }, [availabilityEndpoint, locationFilter, filters, refreshKey])

    const availabilityData = Array.isArray(availabilityState.data?.parts_availability)
        ? availabilityState.data.parts_availability
        : []
    const availabilityTotal = availabilityState.data?.total_quantity ?? 0



    return (
        <>
            <AddOrEdit
                ref={addOrEditRef}
                handleCreate={handleCreate}
                handleUpdate={handleUpdate}
            />
            <div
                className="storage-parts-stats-chart"
            >
                <div className="head">
                    <h3>{trans('tabs.parts')}</h3>
                    <div className="right">
                        <button
                            onClick={() => addOrEditRef.current.add()}
                            className="button button-add"
                            style={{ marginRight: "0" }}
                        >
                            {trans("buttons.new")}
                        </button>
                        <Button onClick={(e) => {
                            compactFilterLayoutRef.current.open(e)
                        }}>
                            <img src={filterImage} alt="Filter" />
                            {getAppliedFiltersCount() ?
                                <i>{getAppliedFiltersCount()}</i>
                                : <></>}
                        </Button>
                        <CompactFilterLayout
                            ref={compactFilterLayoutRef}
                            appliedFiltersCount={getAppliedFiltersCount()}
                            onClearClick={() => setFilters(INITIAL_FILTERS)}
                        >
                            <div className='row'>
                                <div className='col'>
                                    <DateRange
                                        startDate={filters.date_from || ''}
                                        endDate={filters.date_to || ''}
                                        onStartDateChange={(date) => handleFilterChange('date_from', date)}
                                        onEndDateChange={(date) => handleFilterChange('date_to', date)}
                                    />
                                </div>
                            </div>
                        </CompactFilterLayout>
                    </div>
                </div>

                <hr />

                <div className="body">
                    <div className="left">
                        <h5>{trans('random.recentlyAddedOrderedParts')}</h5>
                        <PartsList
                            recentParts={recentParts}
                            loading={state.loading}
                        />
                    </div>
                    <div className="right">
                        <div className='bot'>
                            <div className="bot-top">
                                <h5>{trans('random.partsAvailability')}</h5>
                                <div className="toggle-buttons">
                                    <button
                                        className={locationFilter === 'warehouse' ? 'active' : ''}
                                        onClick={() => setLocationFilter('warehouse')}
                                    >
                                        {trans('random.inWarehouse')}
                                    </button>
                                    <button
                                        className={locationFilter === 'van' ? 'active' : ''}
                                        onClick={() => setLocationFilter('van')}
                                    >
                                        {trans('labels.inVan')}
                                    </button>
                                </div>
                            </div>
                            {availabilityState.loading ?
                                <Skeleton height={150} count={2} />
                                :
                                availabilityTotal === 0 ?
                                    <div
                                        className="alert warning"
                                        style={{
                                            width: '100%',
                                            marginBottom: "auto",
                                        }}
                                    >
                                        {trans("messages.noDataFound")}
                                    </div>
                                    :
                                    <div className="bot-chart">
                                        <PieChart
                                            series={[{
                                                data: availabilityData
                                                    .filter(part => Number(part.quantity || 0) > 0)
                                                    .map((part, index) => ({
                                                        id: index,
                                                        value: part.quantity || 0,
                                                        label: part.part_name,
                                                        color: getColor(index)
                                                    })),
                                            }]}
                                            width={size}
                                            height={size}
                                            margin={{ right: 0 }}
                                            slotProps={{
                                                legend: { hidden: true },
                                            }}
                                            sx={{
                                                '& .MuiPieArc-root': {
                                                    stroke: 'none',
                                                    strokeWidth: 0,
                                                },
                                            }}
                                        />
                                        <table>
                                            <tbody>
                                                {availabilityData.map((part, idx) => (
                                                    <tr key={part.part_name || idx}>
                                                        <td>
                                                            <span style={{ backgroundColor: getColor(idx) }} />
                                                        </td>
                                                        <td><b>{part.part_name}</b></td>
                                                        <td>{part.quantity}</td>
                                                        <td>{part.percentage}%</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}